'use client'

import { useState } from 'react'
import { usePathname } from 'next/navigation'
import { toPublicLocale } from '@/lib/locale-prefix'
import Link from 'next/link'
import { Menu, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LanguageSelector } from '@/components/language-selector'
import { useLocale, useTranslations } from 'next-intl'
import { getCanonicalFamilyLandingHref, getHomeHref, getLocalePrefix } from '@/lib/locale-paths'
import {
  getDesktopNavCharacterLinks,
  getMobileNavCharacterLinks,
  MOBILE_ONLY_CHARACTER_IDS,
} from '@/lib/landing-character-links'
import { getBrand } from '@/lib/brands'
import { isLocaleSelectorEnabled } from '@/lib/locale-capabilities'
import { BrandNavLogo } from '@/components/brand-nav-logo'

type LandingNavProps = {
  /**
   * Dónde viven las secciones que enlaza la nav (#pricing, #how-it-works,
   * #faq): en la propia página (landings) o en el home. Las páginas que usan
   * esta nav sin tener esas secciones —las guías— deben pasar `'home'`: en
   * ellas el ancla suelta no lleva a ninguna parte. Mismo criterio que
   * `LegalNav`, que siempre las apunta al home.
   */
  sections?: 'self' | 'home'
}

export function LandingNav({ sections = 'self' }: LandingNavProps = {}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const pathname = usePathname()
  const locale = useLocale()
  const t = useTranslations('landing.navigation')
  const brand = getBrand()
  const showLanguageSelector = isLocaleSelectorEnabled(pathname)
  const santaHref = getHomeHref(toPublicLocale(locale))
  const papaNoelHref = brand.id === 'raton-perez'
    ? getCanonicalFamilyLandingHref('santa', toPublicLocale(locale))
    : undefined

  // Desktop: clásicas + hub. Las ocasiones (Conejo, Halloween, Cumpleaños) no
  // caben a `lg` en inglés; van solo en el drawer móvil y en el footer.
  const desktopCharacterLinks = getDesktopNavCharacterLinks(locale)
  const mobileCharacterLinks = getMobileNavCharacterLinks(locale)
  const mobileOnlyIds = new Set<string>(MOBILE_ONLY_CHARACTER_IDS)

  // Igual que LegalNav: el ancla se prefija con el locale ('' en es) para salir
  // de la página actual hacia la sección del home.
  const sectionHref = (anchor: string) =>
    sections === 'home' ? `${getLocalePrefix(locale)}/#${anchor}` : `#${anchor}`

  const sectionLinks = [
    { href: sectionHref('pricing'), label: t('pricing') },
    { href: sectionHref('how-it-works'), label: t('howItWorks') },
    { href: sectionHref('faq'), label: t('faq') },
  ]

  return (
    <nav className="relative z-20 border-b border-white/10 bg-black/20 backdrop-blur-sm">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 md:px-10">
        {/* Logo */}
        <Link href={santaHref} className="flex items-center gap-2 md:gap-3">
          <BrandNavLogo />
          <span className="font-christmas text-lg font-bold md:text-xl">{brand.name}</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden items-center gap-6 text-sm lg:flex">
          {desktopCharacterLinks.map((link) => (
            <Link
              key={link.id}
              href={link.href}
              className="font-christmas transition-colors hover:text-amber-200"
            >
              {t(link.labelKey)}
            </Link>
          ))}
          {papaNoelHref && (
            <Link
              href={papaNoelHref}
              className="font-christmas transition-colors hover:text-amber-200"
            >
              {t('otherCharacters')}
            </Link>
          )}
          {sectionLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="font-christmas transition-colors hover:text-amber-200"
            >
              {link.label}
            </Link>
          ))}
          {showLanguageSelector && (
            <LanguageSelector mode="public" variant="compact" className="font-christmas" />
          )}
        </div>

        {/* Mobile actions */}
        <div className="flex items-center gap-2 lg:hidden">
          {showLanguageSelector && (
            <LanguageSelector mode="public" variant="compact" className="font-christmas" />
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="border-t border-white/10 bg-black/30 backdrop-blur-md lg:hidden">
          <div className="mx-auto max-w-7xl px-6 py-4">
            <div className="flex flex-col gap-4">
              {mobileCharacterLinks.map((link) => (
                <Link
                  key={link.id}
                  href={link.href}
                  className={
                    mobileOnlyIds.has(link.id)
                      ? 'font-christmas text-base text-amber-100/90 transition-colors hover:text-amber-200'
                      : 'font-christmas text-base transition-colors hover:text-amber-200'
                  }
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t(link.labelKey)}
                </Link>
              ))}
              {papaNoelHref && (
                <Link
                  href={papaNoelHref}
                  className="font-christmas text-base transition-colors hover:text-amber-200"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('otherCharacters')}
                </Link>
              )}
              {sectionLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="font-christmas text-base transition-colors hover:text-amber-200"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}
